import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import GraphQLErrorText from '../../components/GraphQLErrorText';
import Loading from '../../components/Loading';
import { TidalButton } from '../../components/common/button';
import TidalModal from '../../components/common/modal/TidalModal';
import TidalDashboardAddBotKeyModal from '../../components/tidal-dashboard/bot-vault/TidalDashboardAddBotKeyModal';
import TidalDashboardBotVaultCard from '../../components/tidal-dashboard/bot-vault/TidalDashboardBotVaultCard';
import TidalDashboardDeleteBotKeyModal from '../../components/tidal-dashboard/bot-vault/TidalDashboardDeleteBotKeyModal';
import TidalDashboardHeader from '../../components/tidal-dashboard/common/header';
import useAuthOrRedirect from '../../hooks/useAuthOrRedirect';
import { PROFILE } from '../../lib/graphql/queries';
import TidalDashboardEditBotKeyModal from '../../components/tidal-dashboard/bot-vault/TidalDashboardEditBotKeyModal';
import TidalIconDropdown from '../../components/common/dropdown/TidalIconDropdown';

const TidalDashboardbotsWrapper = styled.div`
  padding-bottom: 100px;
  width: 100%;
`;

const TidalDashboardContainer = styled.div`
  padding: 20px 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(317px, 1fr));
  grid-gap: 24px;
  @media only screen and (max-width: 1100px) {
    grid-gap: 16px;
  }
  @media only screen and (max-width: 540px) {
    grid-gap: 10px;
    grid-template-columns: 1fr;
  }
`;

const TidalDashboardBotVaultHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 307px 189px;
  grid-gap: 8px 19px;
  margin-top: 22px;
  align-items: center;

  @media only screen and (max-width: 1110px) {
    grid-template-columns: 1fr 150px 150px;
  }
  @media only screen and (max-width: 660px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr 1fr;
  }
`;

const TidalDashboardBotVaultHeaderTitle = styled.p`
  font-weight: 700;
  font-size: 21px;
  line-height: 25px;
  color: ${(props) => props.theme.colors.text[1]};
`;

const TidalDashboardbots = (): JSX.Element => {
  const { loading, error } = useQuery(PROFILE);
  const {
    data,
    error: err,
    isValidating,
    mutate,
  } = useSWR('/dashboard/licenses');

  const [isDeleteBotKeyModalVisible, setIsDeleteBoyKeyModalVisible] =
    useState(false);
  const [isAddBotKeyModalVisible, setIsAddBotKeyModalVisible] = useState(false);
  const [isEditBotKeyModalVisible, setIsEditBotkeyModalVisible] =
    useState(false);
  const [availableBots, setAvailableBots] = useState([]);
  const [selectedBotKey, setSelectedBotKey] = useState({
    id: '',
    botName: '',
    botImage: '',
    botId: '',
    botLicenseKey: '',
    purchasePrice: '',
    renewalPrice: '',
    notes: [],
    licenseType: '',
    renewalDate: '',
    renewalInterval: '',
    rentalIntervalTypes: [],
    licenseTypes: [],
    tags: [],
  });

  const [bots, setBots] = useState(data);
  const [filteredBots, setFilteredBots] = useState(data);

  useEffect(() => {
    if (typeof data == 'object') {
      setBots(data);
      setFilteredBots(data);
      let tempBots = data.map((d: any) => {
        return { text: d.software.name, image: d.software.image_url };
      });
      const distinctBots = [];
      tempBots.forEach((b) => {
        let flag = 0;
        distinctBots.forEach((d) => {
          if (b.text == d.text) {
            flag = 1;
          }
        });
        if (flag == 0) distinctBots.push(b);
      });
      // tempBots = new Set(distinctBots);
      setAvailableBots([{ text: 'All bots', image: null }, ...distinctBots]);
    }
  }, [data]);

  const handleFilterKeys = (searchText: string) => {
    let tempBots = [];
    if (searchText == 'All bots') {
      setFilteredBots(bots);
      return;
    }
    bots.forEach(async (bot) => {
      if (bot.software.name == searchText) {
        tempBots.push(bot);
      }
    });
    setFilteredBots(tempBots);
  };

  const handleSetSelectedBot = (bot) => {
    setSelectedBotKey({
      id: bot.id,
      botName: bot.software.name,
      botId: bot.software.id,
      botImage: bot.software.image_url,
      botLicenseKey: bot.last4,
      purchasePrice: bot.purchasePrice,
      renewalPrice: bot.renewalPrice,
      notes: bot.notes,
      renewalDate: bot.renewalDate,
      renewalInterval: bot.renewalInterval,
      licenseType: bot.licenseType,
      licenseTypes: bot.software.license_types,
      tags: bot.tags,
      rentalIntervalTypes: bot.software.license_types.map((_, i) =>
        i.toString(),
      ),
    });
  };

  if (!useAuthOrRedirect()) return null;
  if (loading || (!err && !data)) return <Loading />;
  if (error) return <GraphQLErrorText error={error} />;

  return (
    <TidalDashboardbotsWrapper>
      <TidalModal
        isOpen={isDeleteBotKeyModalVisible}
        onClose={() => {
          setIsDeleteBoyKeyModalVisible(false);
        }}
        Modal={
          <TidalDashboardDeleteBotKeyModal
            botImage={selectedBotKey.botImage}
            botName={selectedBotKey.botName}
            licenseKey={selectedBotKey.botLicenseKey}
            id={selectedBotKey.id}
            onClose={() => {
              setIsDeleteBoyKeyModalVisible(false);
            }}
            onDeleted={() => {
              setIsDeleteBoyKeyModalVisible(false);
              mutate('/dashboard/licenses');
            }}
          />
        }
      />
      <TidalModal
        isOpen={isEditBotKeyModalVisible}
        onClose={() => {
          setIsEditBotkeyModalVisible(false);
        }}
        Modal={
          <TidalDashboardEditBotKeyModal
            botImage={selectedBotKey.botImage}
            botName={selectedBotKey.botName}
            licenseKey={selectedBotKey.botLicenseKey}
            tags={selectedBotKey.tags}
            id={selectedBotKey.id}
            botId={selectedBotKey.botId}
            renewalDate={selectedBotKey.renewalDate}
            purchasePrice={selectedBotKey.purchasePrice}
            renewalPrice={selectedBotKey.renewalPrice}
            notes={selectedBotKey.notes}
            licenseType={selectedBotKey.licenseType}
            renewalInterval={selectedBotKey.renewalInterval}
            licenseTypes={selectedBotKey.licenseTypes}
            renewalIntervalTypes={selectedBotKey.rentalIntervalTypes}
            onClose={() => {
              setIsEditBotkeyModalVisible(false);
            }}
            onAdded={() => {
              setIsEditBotkeyModalVisible(false);
              mutate('/dashboard/licenses');
            }}
          />
        }
      />
      <TidalModal
        isOpen={isAddBotKeyModalVisible}
        onClose={() => {
          setIsAddBotKeyModalVisible(false);
        }}
        Modal={
          <TidalDashboardAddBotKeyModal
            onClose={() => {
              setIsAddBotKeyModalVisible(false);
            }}
            onAdded={() => {
              setIsAddBotKeyModalVisible(false);
              mutate('/dashboard/licenses');
            }}
          />
        }
      />
      <TidalDashboardHeader
        title="Bot vault"
        showSettings={false}
        tags={[{ text: `${filteredBots?.length} total`, isActive: true }]}
      />
      <TidalDashboardBotVaultHeader>
        <TidalDashboardBotVaultHeaderTitle>
          Showing {filteredBots?.length || ''} bot keys
        </TidalDashboardBotVaultHeaderTitle>
        <TidalIconDropdown
          onSelect={(item) => {
            handleFilterKeys(item);
          }}
          label="All Bots"
          items={availableBots}
        />
        <TidalButton
          type="info"
          text="Add Key"
          tint={false}
          onClick={() => {
            setIsAddBotKeyModalVisible(true);
          }}
        />
      </TidalDashboardBotVaultHeader>
      <TidalDashboardContainer>
        {filteredBots &&
          filteredBots?.map((bot) => (
            <TidalDashboardBotVaultCard
              name={bot.software.name}
              image={bot.software.image_url}
              rentalId={bot.software.rental_id}
              expires={bot.renewalDate}
              licenseKey={bot.last4}
              id={bot.id}
              tags={bot.tags}
              startDate={bot.startDate}
              licenseType={bot.licenseType}
              onEdit={() => {
                setIsEditBotkeyModalVisible(true);
                handleSetSelectedBot(bot);
                console.log(bot);
              }}
              onDelete={() => {
                setIsDeleteBoyKeyModalVisible(true);
                handleSetSelectedBot(bot);
              }}
            />
          ))}
      </TidalDashboardContainer>
    </TidalDashboardbotsWrapper>
  );
};

export default TidalDashboardbots;
